import { DownloadSimple } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { FpClassificationMaster } from "@/pages/panchatanthra/fp/FpClassificationMaster";
import { useAuth } from "@/context/AuthContext";
import { EXCEPTION_STATE_TONE, SEVERITY_TONE, fpBase, inr, monthLabel, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const METHODS = [
  ["direct_accounting", "Direct accounting mapping"],
  ["supplementary", "Supplementary source"],
  ["aikyam_interface", "Aikyam interface"],
  ["api", "System / API integration"],
];

const UploadRow = ({ source, period, onDone }) => {
  const { tenantId } = useFpContext();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const here = source.latest_upload?.period === period ? source.latest_upload : null;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(
        `${fpBase(tenantId)}/datasets/upload?source_code=${source.source_code}&period=${period}`,
        body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${source.label}: version ${data.version} stored (${data.rows} rows)`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="text-right">
      <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.txt" className="hidden"
             data-testid={`fp-file-${source.source_code}`}
             onChange={(e) => upload(e.target.files?.[0])} />
      <button onClick={() => fileRef.current?.click()} disabled={busy || !period}
              data-testid={`fp-upload-${source.source_code}`}
              className={`rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.12em] transition-colors disabled:opacity-50 ${
                here ? "border border-border text-navy hover:bg-muted"
                     : "bg-terracotta text-white hover:bg-terracotta/90"}`}>
        {busy ? "Uploading" : here ? "Replace" : "Upload"}
      </button>
      {here ? (
        <p className="mt-1.5 max-w-[15rem] text-[10px] leading-snug text-muted-foreground"
           data-testid={`fp-uploaded-file-${source.source_code}`}>
          <span className="block truncate font-medium text-success" title={here.filename}>
            {here.filename}
          </span>
          v{here.version} · {here.row_count} rows
          {here.origin ? ` · ${here.origin}` : ""}
        </p>
      ) : source.months_uploaded?.length > 0 ? (
        <p className="mt-1.5 text-[10px] text-muted-foreground"
           data-testid={`fp-other-months-${source.source_code}`}>
          nothing for {monthLabel(period)} · have {source.months_uploaded.slice(0, 3).map(monthLabel).join(", ")}
        </p>
      ) : (
        <p className="mt-1.5 text-[10px] text-muted-foreground"
           data-testid={`fp-never-uploaded-${source.source_code}`}>
          never supplied
        </p>
      )}
    </div>
  );
};

export default function FpSources() {
  const { tenantId, period, basis, reloadContext } = useFpContext();
  const { isPlatformAdmin } = useAuth();
  const [sources, setSources] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [model, setModel] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadPeriod, setUploadPeriod] = useState("");
  const [policy, setPolicy] = useState(null);
  const [policyDraft, setPolicyDraft] = useState({});
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [showUnused, setShowUnused] = useState(false);

  const editPolicy = (kind, patch) =>
    setPolicyDraft((d) => ({ ...d, [kind]: { ...(d[kind] || {}), ...patch } }));

  const savePolicy = async () => {
    setSavingPolicy(true);
    const items = {};
    (policy?.types || []).forEach((t) => {
      const draft = policyDraft[t.kind] || {};
      items[t.kind] = {
        severity: draft.severity ?? t.severity,
        threshold: Number(draft.threshold ?? t.threshold ?? 0) || 0,
        owner: draft.owner ?? t.owner ?? "",
      };
    });
    try {
      await api.put(`${fpBase(tenantId)}/exception-policy`, { items });
      toast.success("Exception policy saved");
      setPolicyDraft({});
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingPolicy(false); }
  };

  const dispose = async (row, action) => {
    let reason = "";
    if (action !== "reopen") {
      reason = window.prompt(
        action === "accept"
          ? `Accept "${row.value}" permanently. Why is this correct to leave outside the figures?`
          : `Park "${row.value}" for this month only. Why?`) || "";
      if (!reason.trim()) return;
    }
    try {
      await api.post(`${fpBase(tenantId)}/exceptions/dispose`, {
        exception_key: row.exception_key, action, reason, period: uploadPeriod || period });
      toast.success(action === "reopen" ? "Exception reopened" : `Exception ${action}ed`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [s, d, p] = await Promise.all([
        api.get(`${fpBase(tenantId)}/sources`, { params: uploadPeriod ? { period: uploadPeriod } : {} }),
        api.get(`${fpBase(tenantId)}/datasets`),
        api.get(`${fpBase(tenantId)}/exception-policy`),
      ]);
      setSources(s.data);
      setDatasets(d.data);
      setPolicy(p.data);
      if (period) {
        const m = await api.get(`${fpBase(tenantId)}/model`, { params: { period, basis } });
        setModel(m.data);
      }
    } catch (e) { toast.error(errMsg(e)); }
  }, [tenantId, period, basis, uploadPeriod]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (period && !uploadPeriod) setUploadPeriod(period); }, [period, uploadPeriod]);

  const saveSource = async (source, patch) => {
    try {
      await api.put(`${fpBase(tenantId)}/sources/${source.source_code}`, {
        source_code: source.source_code, name: source.name || source.label,
        method: source.method || "aikyam_interface", owner: source.owner || "",
        refresh_frequency: source.refresh_frequency || "Monthly",
        control_item: source.control_item || "", notes: source.notes || "",
        active: source.active !== false, ...patch });
      toast.success(`${source.label} registered`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeDataset = async (id) => {
    try {
      await api.delete(`${fpBase(tenantId)}/datasets/${id}`);
      toast.success("Uploaded statement removed");
      load();
      reloadContext();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`${fpBase(tenantId)}/datasets/${id}`);
      setPreview(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const supplied = sources.filter((s) => s.latest_upload?.period === uploadPeriod);
  // a source is only *asked of the client* when a readiness rule requires it, the client
  // registered it, or it has been supplied before — and never when the engine builds it
  // itself from the monthly workbook package
  const wanted = (s) => !s.derived_from_workbooks &&
    (s.required_for.length > 0 || s.registered || s.used_before);
  const needed = sources.filter((s) => s.latest_upload?.period !== uploadPeriod && wanted(s)
    && (s.registered || s.used_before))
    .sort((a, b) => (b.required_for.length > 0) - (a.required_for.length > 0));
  const enginePending = sources.filter((s) => s.latest_upload?.period !== uploadPeriod
    && s.derived_from_workbooks);
  const unused = sources.filter((s) => s.latest_upload?.period !== uploadPeriod
    && !s.derived_from_workbooks && !wanted(s));

  const monthOptions = (() => {
    const base = uploadPeriod || period || new Date().toISOString().slice(0, 7);
    const startYear = Number(base.slice(0, 4)) - (Number(base.slice(5, 7)) >= 4 ? 0 : 1);
    return [...Array(24)].map((_, i) => {
      const month = 4 + i;
      const y = startYear + Math.floor((month - 1) / 12);
      const m = ((month - 1) % 12) + 1;
      return `${y}-${String(m).padStart(2, "0")}`;
    });
  })();

  return (
    <FpShell
      title="Source registry & monthly statements"
      description="Register how each Level-3 requirement is sourced — directly from accounting, from a supplementary system, through an Aikyam interface or a future API — then upload that month's statement. Every upload is a new immutable version."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <a href="/downloads/FP_Input_Templates_Starter_Pack.zip" download
             data-testid="fp-sources-download-templates"
             className="inline-flex items-center gap-2 rounded-full bg-terracotta px-4 py-2 text-xs font-semibold text-white transition hover:bg-terracotta/90">
            <DownloadSimple size={14} />
            Excel Templates (.zip)
          </a>
          <a href="/downloads/FP_Framework_Input_and_Configuration_Guide.docx" download
             data-testid="fp-sources-download-guide"
             className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold text-navy transition hover:bg-muted">
            <DownloadSimple size={14} />
            Framework Guide (.docx)
          </a>
        </div>
      }
    >
      <div className="space-y-6" data-testid="fp-sources">
        <div className="panel p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="label-caps">Upload month</p>
              <p className="mt-1 text-sm text-foreground/75">
                Statements are uploaded month by month. Choose the month, then upload against each source.
              </p>
            </div>
            <select value={uploadPeriod} onChange={(e) => setUploadPeriod(e.target.value)}
                    data-testid="fp-upload-period"
                    className="rounded-full border border-border bg-card px-4 py-2 text-xs text-navy outline-none focus:border-terracotta">
              {monthOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </div>
        </div>

        <FpClassificationMaster tenantId={tenantId} canConfigure={isPlatformAdmin} />

        <div className="panel overflow-hidden">
          <div className="border-b border-border px-6 py-4">
            <p className="label-caps">Source &amp; interface registry</p>
            <h3 className="font-display text-lg text-navy">
              What has been supplied for {monthLabel(uploadPeriod)}, and what is still wanted
            </h3>
            <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-muted-foreground">
              <span data-testid="fp-sources-count-supplied">
                <span className="font-semibold text-success">{supplied.length} supplied</span>
                {" "}for this month
              </span>
              <span data-testid="fp-sources-count-needed">
                <span className="font-semibold text-[hsl(30_88%_28%)]">{needed.length} still needed</span>
                {" "}— {needed.filter((s) => s.required_for.length).length} of them block Level 3
              </span>
              {enginePending.length > 0 && (
                <span data-testid="fp-sources-count-engine">
                  <span className="font-semibold text-navy">{enginePending.length} built by the engine</span>
                  {" "}from the workbook package — never uploaded by hand
                </span>
              )}
            </div>
          </div>
          {[["Still needed for this month", needed, "needed"],
            ["Built by the engine from the monthly workbooks — nothing to upload", enginePending, "engine"],
            ["Already supplied for this month", supplied, "supplied"]].map(([heading, list, key]) => (
            list.length > 0 && (
              <div key={key} data-testid={`fp-sources-group-${key}`}>
                <p className="label-caps bg-muted/40 px-6 py-2">{heading} · {list.length}</p>
                <div className="divide-y divide-border">
                  {list.map((s) => (
                    <div key={s.source_code} className="grid grid-cols-1 gap-3 px-6 py-4 md:grid-cols-[1.4fr_1.2fr_1fr_auto] md:items-center"
                         data-testid={`fp-source-${s.source_code}`}>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-navy">{s.label}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${
                            s.uploaded ? "bg-success/18 text-success"
                                       : "bg-warning/25 text-[hsl(30_88%_30%)]"}`}
                                data-testid={`fp-source-status-${s.source_code}`}>
                            {s.uploaded ? "supplied" : "not supplied"}
                          </span>
                          {s.required_for.length > 0 && (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
                              needed for {s.required_for.join(", ")}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {s.role === "control" ? "Accounting control layer"
                            : s.role === "budget" ? "Budget" : "Supplementary detail"}
                          {s.level3_dimensions.length > 0
                            ? ` · must carry ${s.level3_dimensions.map((d) => d.replace(/_/g, " ")).join(", ")}`
                            : ` · ${s.fields.slice(0, 4).join(", ")}`}
                        </p>
                      </div>
                      <select value={s.method || "aikyam_interface"}
                              onChange={(e) => saveSource(s, { method: e.target.value })}
                              data-testid={`fp-source-method-${s.source_code}`}
                              className="rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta">
                        {METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                      <p className="text-[11px] text-muted-foreground">
                        {key === "engine"
                          ? "Produced automatically when the monthly workbooks land"
                          : s.registered ? `Registered · control: ${s.control_item || "—"}` : "Not registered"}
                      </p>
                      {key === "engine" ? (
                        <span className="justify-self-start rounded-full bg-muted px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] text-muted-foreground"
                              data-testid={`fp-source-engine-built-${s.source_code}`}>
                          Engine-built
                        </span>
                      ) : (
                        <UploadRow source={s} period={uploadPeriod}
                                   onDone={() => { load(); reloadContext(); }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
          {unused.length > 0 && (
            <div data-testid="fp-sources-unused">
              <button onClick={() => setShowUnused(!showUnused)}
                      data-testid="fp-sources-unused-toggle"
                      className="flex w-full items-center justify-between bg-muted/40 px-6 py-2 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground hover:bg-muted/60">
                <span>Not used by this client — available if ever needed · {unused.length}</span>
                <span>{showUnused ? "Hide" : "Show"}</span>
              </button>
              {showUnused && (
                <div className="divide-y divide-border" data-testid="fp-sources-unused-list">
                  {unused.map((s) => (
                    <div key={s.source_code} className="grid grid-cols-1 gap-3 px-6 py-4 md:grid-cols-[1.4fr_1.2fr_1fr_auto] md:items-center"
                         data-testid={`fp-source-${s.source_code}`}>
                      <div>
                        <p className="text-sm font-semibold text-navy">{s.label}</p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {s.role === "control" ? "Accounting control layer"
                            : s.role === "budget" ? "Budget" : "Supplementary detail"} ·
                          this client's monthly package does not call for it
                        </p>
                      </div>
                      <select value={s.method || "aikyam_interface"}
                              onChange={(e) => saveSource(s, { method: e.target.value })}
                              data-testid={`fp-source-method-${s.source_code}`}
                              className="rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta">
                        {METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                      <p className="text-[11px] text-muted-foreground">Not registered</p>
                      <UploadRow source={s} period={uploadPeriod}
                                 onDone={() => { load(); reloadContext(); }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="panel overflow-hidden" data-testid="fp-dataset-list">
          <div className="border-b border-border px-6 py-4">
            <p className="label-caps">Upload history</p>
            <h3 className="font-display text-lg text-navy">Every version ever supplied, kept</h3>
            <p className="mt-1 max-w-3xl text-[11px] text-muted-foreground">
              Uploads are never overwritten. Re-uploading the same statement for a month stores a
              new version and the engine uses the newest one, so a figure published last week can
              always be explained by the exact file it came from.{" "}
              <span className="text-navy">Drill down</span> opens the actual rows of that file as
              the engine read them.{" "}
              <span className="text-navy">Remove</span> deletes that one version and recomputes the
              month from what is left — use it when a wrong file was uploaded, not to tidy up.
            </p>
          </div>
          {!datasets.length ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">Nothing uploaded yet.</p>
          ) : (
            <div className="max-h-[28rem] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3 text-left">Month</th>
                    <th className="px-5 py-3 text-left">Source</th>
                    <th className="px-5 py-3 text-left">File</th>
                    <th className="px-5 py-3 text-right">Version</th>
                    <th className="px-5 py-3 text-right">Rows</th>
                    <th className="px-5 py-3 text-left">Uploaded by</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {datasets.map((d) => (
                    <tr key={d.id} data-testid={`fp-dataset-${d.id}`}>
                      <td className="px-5 py-3 text-navy">{monthLabel(d.period)}</td>
                      <td className="px-5 py-3 text-navy">{d.source_code}</td>
                      <td className="max-w-[16rem] truncate px-5 py-3 text-[11px] text-muted-foreground"
                          title={d.filename || "Keyed entry"}>
                        {d.filename || (d.origin ? `${d.origin}` : "Keyed entry")}
                      </td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums">{d.version}</td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums">{d.row_count}</td>
                      <td className="px-5 py-3 text-[11px] text-muted-foreground">
                        {d.uploaded_by_email} · {String(d.uploaded_at || "").slice(0, 10)}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => openPreview(d.id)} data-testid={`fp-dataset-view-${d.id}`}
                                className="rounded-full border border-border px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] text-navy hover:bg-muted">
                          Drill down
                        </button>
                        <button onClick={() => removeDataset(d.id)} data-testid={`fp-dataset-delete-${d.id}`}
                                className="ml-2 rounded-full border border-destructive/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] text-destructive hover:bg-destructive/10">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {model && (
          <div className="panel overflow-hidden" data-testid="fp-exception-policy">
            <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border px-6 py-4">
              <div>
                <p className="label-caps">Exception handling</p>
                <h3 className="font-display text-lg text-navy">How each kind is treated</h3>
                <p className="mt-1 max-w-3xl text-[11px] text-muted-foreground">
                  An exception is value the engine could not place, so it sits outside every
                  published figure. Choose whether each kind holds back Level 3, set an amount
                  below which it is not worth raising, and name who owns clearing it. Nothing is
                  ever deleted — suppressed items are still listed, marked.
                </p>
              </div>
              {policy?.can_configure && (
                <button onClick={savePolicy} disabled={savingPolicy} data-testid="fp-exception-policy-save"
                        className="rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.12em] text-white hover:bg-terracotta/90 disabled:opacity-50">
                  {savingPolicy ? "Saving" : "Save policy"}
                </button>
              )}
            </div>
            <div className="divide-y divide-border">
              {(policy?.types || []).map((t) => (
                <div key={t.kind} className="grid grid-cols-1 gap-3 px-6 py-4 md:grid-cols-[1.6fr_auto_auto_1fr] md:items-center"
                     data-testid={`fp-exception-type-${t.kind}`}>
                  <div>
                    <p className="text-sm font-semibold text-navy">{t.label}</p>
                    <p className="text-[11px] text-muted-foreground">{t.meaning}</p>
                    <p className="text-[11px] text-terracotta">{t.remedy}</p>
                  </div>
                  <select value={(policyDraft[t.kind]?.severity) ?? t.severity}
                          disabled={!policy?.can_configure}
                          onChange={(e) => editPolicy(t.kind, { severity: e.target.value })}
                          data-testid={`fp-exception-severity-${t.kind}`}
                          className="rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta">
                    <option value="block">Blocks Level 3</option>
                    <option value="warn">Warns only</option>
                    <option value="ignore">Ignore</option>
                  </select>
                  <input type="number" min="0"
                         value={(policyDraft[t.kind]?.threshold) ?? t.threshold ?? 0}
                         disabled={!policy?.can_configure}
                         onChange={(e) => editPolicy(t.kind, { threshold: e.target.value })}
                         data-testid={`fp-exception-threshold-${t.kind}`}
                         placeholder="0"
                         className="w-28 rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta"
                         title="Raise only above this amount" />
                  <input value={(policyDraft[t.kind]?.owner) ?? t.owner ?? ""}
                         disabled={!policy?.can_configure}
                         onChange={(e) => editPolicy(t.kind, { owner: e.target.value })}
                         data-testid={`fp-exception-owner-${t.kind}`}
                         placeholder="Owner (who clears it)"
                         className="rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta" />
                </div>
              ))}
            </div>
          </div>
        )}

        {model?.exceptions?.length > 0 && (
          <div className="panel overflow-hidden border-l-4 border-warning" data-testid="fp-exceptions">
            <div className="border-b border-border px-6 py-4">
              <p className="label-caps">Exceptions</p>
              <h3 className="font-display text-lg text-navy">Value the engine could not place</h3>
              <p className="mt-1 max-w-3xl text-[11px] text-muted-foreground">
                Nothing listed here is inside any published figure. An exception is raised when a
                ledger or interface line has no approved mapping, when a statement line is not yet
                classified, or when the interface detail does not add up to the accounting control
                total. They are never silently absorbed — clear them by approving a mapping under
                Classification review, or by supplying the missing sheet above.
              </p>
            </div>
            <div className="divide-y divide-border">
              {model.exceptions.map((x, i) => (
                <div key={x.exception_key || i} className="px-6 py-3"
                     data-testid={`fp-exception-${x.exception_key || i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-navy">{x.source} · {x.value}</p>
                      <p className="text-[11px] text-muted-foreground">{x.issue}</p>
                      {x.state_reason && (
                        <p className="text-[11px] text-muted-foreground/80">{x.state_reason}</p>
                      )}
                      {x.disposed_by && (
                        <p className="text-[10px] text-muted-foreground/70">
                          {x.state} by {x.disposed_by} · {String(x.disposed_at || "").slice(0, 10)}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {x.severity && (
                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${SEVERITY_TONE[x.severity]}`}>
                          {x.severity === "block" ? "blocks level 3" : x.severity}
                        </span>
                      )}
                      {x.state && (
                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${EXCEPTION_STATE_TONE[x.state]}`}
                              data-testid={`fp-exception-state-${x.exception_key || i}`}>
                          {x.state}
                        </span>
                      )}
                      {x.owner && (
                        <span className="text-[10px] text-muted-foreground">owner {x.owner}</span>
                      )}
                      <p className="font-mono text-xs tabular-nums text-foreground/70">{inr(x.amount)}</p>
                      {x.state === "open" ? (
                        <>
                          <button onClick={() => dispose(x, "accept")}
                                  data-testid={`fp-exception-accept-${x.exception_key}`}
                                  className="rounded-full border border-border px-3 py-1 text-[9px] uppercase tracking-[0.1em] text-navy hover:bg-muted">
                            Accept
                          </button>
                          <button onClick={() => dispose(x, "park")}
                                  data-testid={`fp-exception-park-${x.exception_key}`}
                                  className="rounded-full border border-border px-3 py-1 text-[9px] uppercase tracking-[0.1em] text-navy hover:bg-muted">
                            Park
                          </button>
                        </>
                      ) : (x.state === "accepted" || x.state === "parked") && (
                        <button onClick={() => dispose(x, "reopen")}
                                data-testid={`fp-exception-reopen-${x.exception_key}`}
                                className="rounded-full border border-border px-3 py-1 text-[9px] uppercase tracking-[0.1em] text-navy hover:bg-muted">
                          Reopen
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {preview && (
          <div className="panel overflow-hidden" data-testid="fp-dataset-preview">
            <div className="flex items-center justify-between gap-3 border-b border-border px-6 py-4">
              <div>
                <p className="label-caps">Traceability · {preview.source_code} · {monthLabel(preview.period)} · v{preview.version}</p>
                <h3 className="font-display text-lg text-navy">{preview.filename || "Keyed entry"}</h3>
              </div>
              <button onClick={() => setPreview(null)} data-testid="fp-preview-close"
                      className="rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.1em] text-navy hover:bg-muted">
                Close
              </button>
            </div>
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  <tr>{Object.keys(preview.rows?.[0] || {}).map((h) => (
                    <th key={h} className="px-4 py-2 text-left">{h}</th>))}</tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(preview.rows || []).slice(0, 200).map((r, i) => (
                    <tr key={i}>{Object.keys(preview.rows[0]).map((h) => (
                      <td key={h} className="px-4 py-2 text-navy">{String(r[h] ?? "")}</td>))}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </FpShell>
  );
}
